import React, { useContext } from 'react';
import classNames from 'classnames';

import { JobSearchContext } from '../../context';
import FeaturedOrg from '../featured_org/featured_org';
import FeaturedSection from '../featured_section/featured_section';
import TopEmployers from '../top_employers/top_employers';
import AdButler from '../../../components/react/adbutler/adbutler';
import ErrorBoundary from '../../../components/react/error_boundary/error_boundary';
import JobItem from '../../../components/react/job_item/job_item';
import { Job } from '../../types';
import styles from './result_section.module.scss';

const ResultSection = () => {
  const {
    landingPageInfo,
    deviceInfo,
    userInfo,
    adbutlerInfo,
    featuredJobs,
    nonFeaturedJobResults,
    isLoading,
    returnToValue,
    setFeaturedJobs,
    setNonFeaturedJobResults,
  } = useContext(JobSearchContext);
  const { isSignedIn } = userInfo;
  const isDesktop = !(deviceInfo.isMobile || deviceInfo.isTablet);

  const { featured_organization_visible, featured_section_visible, topEmployers } = landingPageInfo;

  const renderMobileTopAd: boolean = !featured_organization_visible && !isDesktop;
  const separatorIndex: number = Math.ceil(nonFeaturedJobResults.length / 4);
  const jobAsSets: Job[] = [...nonFeaturedJobResults];
  const firstSet: Job[] = jobAsSets.splice(0, separatorIndex);
  const secondSet: Job[] = jobAsSets.splice(0, separatorIndex);
  const thirdSet: Job[] = jobAsSets.splice(0, separatorIndex);
  const fourthSet: Job[] = jobAsSets;

  const renderInlineAdButler = (
    <AdButler
      adbutlerInfo={{ ...adbutlerInfo, zone: adbutlerInfo.inlineZone, place: 0 }}
      isSignedIn={isSignedIn}
      display={nonFeaturedJobResults.length > 3}
    />
  );

  const renderFeaturedJobs = (
    <ul
      aria-label='featured-jobs-section'
      className={classNames(`${styles.topBorderList} featured-jobs`, {
        loading: isLoading,
        'no-results': !featuredJobs.length,
      })}
    >
      {featuredJobs.map((job) => (
        <ErrorBoundary key={job.id}>
          <li>
            <JobItem
              job={job}
              userInfo={userInfo}
              returnToValue={returnToValue}
              featuredJobs={featuredJobs}
              setFeaturedJobs={setFeaturedJobs}
              nonFeaturedJobs={nonFeaturedJobResults}
              setNonFeaturedJobs={setNonFeaturedJobResults}
            />
          </li>
        </ErrorBoundary>
      ))}
    </ul>
  );

  const renderFirstSet = (
    <ul
      aria-label='first-non-featured-jobs-set'
      className={classNames(styles.topBorderList, { loading: isLoading })}
    >
      {firstSet.map((job) => (
        <ErrorBoundary key={job.id}>
          <li>
            <JobItem
              job={job}
              userInfo={userInfo}
              returnToValue={returnToValue}
              featuredJobs={featuredJobs}
              setFeaturedJobs={setFeaturedJobs}
              nonFeaturedJobs={nonFeaturedJobResults}
              setNonFeaturedJobs={setNonFeaturedJobResults}
            />
          </li>
        </ErrorBoundary>
      ))}
    </ul>
  );

  const renderSecondSet = (
    <ul
      aria-label='second-non-featured-jobs-set'
      className={classNames(styles.topBorderList, {
        loading: isLoading,
        'no-adbutler-inlined': nonFeaturedJobResults.length < 4 && nonFeaturedJobResults.length > 1,
      })}
    >
      {secondSet.map((job) => (
        <ErrorBoundary key={job.id}>
          <li>
            <JobItem
              job={job}
              userInfo={userInfo}
              returnToValue={returnToValue}
              featuredJobs={featuredJobs}
              setFeaturedJobs={setFeaturedJobs}
              nonFeaturedJobs={nonFeaturedJobResults}
              setNonFeaturedJobs={setNonFeaturedJobResults}
            />
          </li>
        </ErrorBoundary>
      ))}
    </ul>
  );

  const renderThirdSet = (
    <ul
      aria-label='third-non-featured-jobs-set'
      className={classNames(styles.topBorderList, { loading: isLoading })}
    >
      {thirdSet.map((job) => (
        <ErrorBoundary key={job.id}>
          <li>
            <JobItem
              job={job}
              userInfo={userInfo}
              returnToValue={returnToValue}
              featuredJobs={featuredJobs}
              setFeaturedJobs={setFeaturedJobs}
              nonFeaturedJobs={nonFeaturedJobResults}
              setNonFeaturedJobs={setNonFeaturedJobResults}
            />
          </li>
        </ErrorBoundary>
      ))}
    </ul>
  );

  const renderFourthSet = (
    <ul
      aria-label='fourth-non-featured-jobs-set'
      className={classNames(styles.topBorderList, { loading: isLoading })}
    >
      {fourthSet.map((job) => (
        <ErrorBoundary key={job.id}>
          <li>
            <JobItem
              job={job}
              userInfo={userInfo}
              returnToValue={returnToValue}
              featuredJobs={featuredJobs}
              setFeaturedJobs={setFeaturedJobs}
              nonFeaturedJobs={nonFeaturedJobResults}
              setNonFeaturedJobs={setNonFeaturedJobResults}
            />
          </li>
        </ErrorBoundary>
      ))}
    </ul>
  );

  return (
    <div data-testid='result-section'>
      {renderFeaturedJobs}
      {featured_organization_visible && <FeaturedOrg />}
      {renderMobileTopAd && (
        <AdButler
          adbutlerInfo={{ ...adbutlerInfo, zone: adbutlerInfo.sidebarZone, place: 0 }}
          isSignedIn={isSignedIn}
        />
      )}
      {!!firstSet.length && renderFirstSet}
      {featured_section_visible && <FeaturedSection />}
      {!!secondSet.length && renderSecondSet}
      {renderInlineAdButler}
      {!!thirdSet.length && renderThirdSet}
      {!!topEmployers.length && !isDesktop && <TopEmployers />}
      {!!fourthSet.length && renderFourthSet}
    </div>
  );
};

export default ResultSection;
